/**
 * Système d'intensités de couleurs
 *
 * Génère automatiquement 4 niveaux d'intensité pour chaque couleur de la palette.
 * Les intensités sont pré-calculées au chargement pour des performances optimales.
 *
 * Niveaux d'intensité :
 * - soft (0.08) : Surfaces, arrière-plans de cartes
 * - medium (0.16) : Badges, tags, hover states
 * - bold (0.28) : Bordures actives, accents moyens
 * - intense (0.40) : Accents forts, focus states
 *
 * @example
 * import { INTENSITIES, getIntensity, getGradientStyle } from 'packages/common/theme';
 *
 * // Accès direct aux intensités pré-calculées
 * bgcolor: INTENSITIES.purple.soft
 * bgcolor: INTENSITIES.teal.medium
 *
 * // Via helper avec validation
 * bgcolor: getIntensity('purple', 'medium')
 *
 * // Gradient CSS prêt à l'emploi
 * background: getGradientStyle('indigo')
 *
 * @module intensities
 * @author Équipe Brydge
 * @since 2.5.0
 */

import {
  PRIMARY,
  SECONDARY,
  INFO,
  SUCCESS,
  WARNING,
  ERROR,
  PURPLE,
  PINK,
  TEAL,
  INDIGO,
  SLATE,
  AMBER,
  CYAN,
  SKY,
  BLUE,
  ROSE,
} from './palette';

// ============================================================================
// CONFIGURATION DES NIVEAUX D'INTENSITÉ
// ============================================================================

/**
 * Définition des niveaux d'intensité avec leur opacité
 * Ordre croissant d'intensité visuelle
 *
 * @constant {Object.<string, number>}
 */
export const INTENSITY_LEVELS = {
  soft: 0.08,
  medium: 0.16,
  bold: 0.28,
  intense: 0.40,
};

/**
 * Liste des clés d'intensité pour itération et validation
 *
 * @constant {string[]}
 */
export const INTENSITY_KEYS = Object.keys(INTENSITY_LEVELS);

/**
 * Toutes les couleurs supportées pour le système d'intensités
 *
 * @private
 * @constant {Object}
 */
const ALL_COLORS = {
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  purple: PURPLE,
  pink: PINK,
  teal: TEAL,
  indigo: INDIGO,
  slate: SLATE,
  amber: AMBER,
  cyan: CYAN,
  sky: SKY,
  blue: BLUE,
  rose: ROSE,
};

/**
 * Liste des clés de couleurs pour itération et validation
 *
 * @constant {string[]}
 */
export const COLOR_KEYS = Object.keys(ALL_COLORS);

// ============================================================================
// FONCTIONS DE GÉNÉRATION (PRIVÉES)
// ============================================================================

/**
 * Convertit une couleur HEX en composantes RGB
 *
 * @private
 * @param {string} hex - Couleur au format #RRGGBB ou #RGB
 * @return {{ r: number, g: number, b: number }} Composantes RGB
 * @throws {Error} Si le format HEX est invalide
 *
 * @example
 * hexToRgb('#8B5CF6') // { r: 139, g: 92, b: 246 }
 * hexToRgb('#F00')    // { r: 255, g: 0, b: 0 }
 */
function hexToRgb(hex) {
  const cleanHex = hex.replace('#', '');

  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    return {r, g, b};
  }

  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return {r, g, b};
  }

  throw new Error(`Invalid HEX color format: ${hex}`);
}

/**
 * Génère une couleur RGBA à partir d'une couleur HEX et d'une opacité
 *
 * @private
 * @param {string} hex - Couleur au format HEX
 * @param {number} alpha - Opacité entre 0 et 1
 * @return {string} Couleur au format rgba(r, g, b, a)
 *
 * @example
 * createRgba('#8B5CF6', 0.08) // 'rgba(139, 92, 246, 0.08)'
 */
function createRgba(hex, alpha) {
  const {r, g, b} = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Génère toutes les intensités pour une couleur donnée
 *
 * @private
 * @param {Object} colorConfig - Configuration de la couleur (lighter, light, main, etc.)
 * @return {Object} Objet avec les 4 niveaux d'intensité + gradient
 *
 * @example
 * generateColorIntensities(PURPLE)
 * // {
 * //   soft: 'rgba(139, 92, 246, 0.08)',
 * //   medium: 'rgba(139, 92, 246, 0.16)',
 * //   bold: 'rgba(139, 92, 246, 0.28)',
 * //   intense: 'rgba(139, 92, 246, 0.40)',
 * //   gradient: ['#8B5CF6', '#7C3AED']
 * // }
 */
function generateColorIntensities(colorConfig) {
  const {main, dark} = colorConfig;

  const intensities = {};

  Object.entries(INTENSITY_LEVELS).forEach(([level, opacity]) => {
    intensities[level] = createRgba(main, opacity);
  });

  intensities.gradient = [main, dark];

  return intensities;
}

/**
 * Génère les intensités pour toutes les couleurs de la palette
 *
 * @private
 * @return {Object} Map de toutes les couleurs avec leurs intensités
 */
function generateAllIntensities() {
  const result = {};

  COLOR_KEYS.forEach((colorKey) => {
    result[colorKey] = generateColorIntensities(ALL_COLORS[colorKey]);
  });

  return result;
}

// ============================================================================
// EXPORTS PRINCIPAUX
// ============================================================================

/**
 * Toutes les intensités pré-calculées pour chaque couleur
 *
 * Structure :
 * ```
 * INTENSITIES.primary.soft     // 'rgba(7, 141, 238, 0.08)'
 * INTENSITIES.purple.medium    // 'rgba(139, 92, 246, 0.16)'
 * INTENSITIES.teal.bold        // 'rgba(20, 184, 166, 0.28)'
 * INTENSITIES.indigo.intense   // 'rgba(99, 102, 241, 0.40)'
 * INTENSITIES.pink.gradient    // ['#EC4899', '#DB2777']
 * ```
 *
 * @constant {Object.<string, Object>}
 *
 * @example
 * // Fond pastelle pour une card
 * <Card sx={{ bgcolor: INTENSITIES.purple.soft }}>
 *
 * // Badge avec couleur moyenne
 * <Chip sx={{ bgcolor: INTENSITIES.teal.medium }}>
 *
 * // Bordure accentuée
 * <Box sx={{ borderColor: INTENSITIES.indigo.bold }}>
 */
export const INTENSITIES = generateAllIntensities();

// ============================================================================
// HELPERS PUBLICS
// ============================================================================

/**
 * Récupère une intensité spécifique pour une couleur avec validation
 *
 * @param {string} colorName - Nom de la couleur (primary, purple, teal, etc.)
 * @param {string} level - Niveau d'intensité (soft, medium, bold, intense, gradient)
 * @return {string|string[]} Couleur RGBA ou tuple gradient
 * @throws {Error} Si la couleur ou le niveau n'existe pas
 *
 * @example
 * getIntensity('purple', 'soft')     // 'rgba(139, 92, 246, 0.08)'
 * getIntensity('teal', 'gradient')   // ['#14B8A6', '#0D9488']
 */
export function getIntensity(colorName, level) {
  if (!INTENSITIES[colorName]) {
    throw new Error(
        `Unknown color: "${colorName}". Available colors: ${COLOR_KEYS.join(', ')}`,
    );
  }

  const validLevels = [...INTENSITY_KEYS, 'gradient'];
  if (!validLevels.includes(level)) {
    throw new Error(
        `Unknown intensity level: "${level}". Available levels: ${validLevels.join(', ')}`,
    );
  }

  return INTENSITIES[colorName][level];
}

/**
 * Récupère le gradient d'une couleur (raccourci pour getIntensity(color, 'gradient'))
 *
 * @param {string} colorName - Nom de la couleur
 * @return {[string, string]} Tuple [main, dark] pour le gradient
 *
 * @example
 * const [start, end] = getGradient('indigo');
 * // start: '#6366F1', end: '#4F46E5'
 */
export function getGradient(colorName) {
  return getIntensity(colorName, 'gradient');
}

/**
 * Génère un style de gradient CSS prêt à l'emploi
 *
 * @param {string} colorName - Nom de la couleur
 * @param {number} [angle=135] - Angle du gradient en degrés
 * @return {string} Valeur CSS pour la propriété background
 *
 * @example
 * // Gradient standard (135deg)
 * background: getGradientStyle('purple')
 * // → 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
 *
 * // Gradient vertical
 * background: getGradientStyle('teal', 180)
 * // → 'linear-gradient(180deg, #14B8A6 0%, #0D9488 100%)'
 *
 * // Gradient horizontal
 * background: getGradientStyle('indigo', 90)
 * // → 'linear-gradient(90deg, #6366F1 0%, #4F46E5 100%)'
 */
export function getGradientStyle(colorName, angle = 135) {
  const [start, end] = getGradient(colorName);
  return `linear-gradient(${angle}deg, ${start} 0%, ${end} 100%)`;
}

/**
 * Crée une intensité personnalisée avec une opacité spécifique
 * Utile pour des cas particuliers hors des 4 niveaux standard
 *
 * @param {string} colorName - Nom de la couleur
 * @param {number} opacity - Opacité personnalisée entre 0 et 1
 * @return {string} Couleur RGBA
 * @throws {Error} Si la couleur n'existe pas ou l'opacité est invalide
 *
 * @example
 * // Opacité très légère pour un overlay
 * background: createCustomIntensity('purple', 0.03)
 * // → 'rgba(139, 92, 246, 0.03)'
 *
 * // Opacité forte pour un highlight
 * background: createCustomIntensity('teal', 0.5)
 * // → 'rgba(20, 184, 166, 0.5)'
 */
export function createCustomIntensity(colorName, opacity) {
  if (!ALL_COLORS[colorName]) {
    throw new Error(
        `Unknown color: "${colorName}". Available colors: ${COLOR_KEYS.join(', ')}`,
    );
  }

  if (typeof opacity !== 'number' || opacity < 0 || opacity > 1) {
    throw new Error(`Opacity must be a number between 0 and 1, got: ${opacity}`);
  }

  return createRgba(ALL_COLORS[colorName].main, opacity);
}
